/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Text,
  Depths,
  DetailsList,
  SelectionMode,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Icon,
} from '@fluentui/react';
import { useMutation } from 'react-query';
import moment from 'moment';
import Stepper from '../Stepper';
import * as sapi from '../../services/sapi';
import useRestartIngestorMutation from '../../hooks/useRestartIngestorMutation';
import theme from '../../../../theme.style';

const columns = [
  {
    key: 'productId',
    name: 'ProductId',
    ariaLabel: 'productId',
    fieldName: 'productId',
    minWidth: 100,
  },
  {
    key: 'stagingQuantity',
    name: 'Staging Qty.',
    ariaLabel: 'stagingQuantity',
    fieldName: 'stagingQuantity',
    minWidth: 100,
  },
  {
    key: 'stagedTxsQuantity',
    name: 'Staged Txs Qty.',
    ariaLabel: 'stagedTxsQuantity',
    fieldName: 'stagedTxsQuantity',
    minWidth: 100,
  },
  {
    key: 'nrbTxsQuantity',
    name: 'NRB Qty.',
    ariaLabel: 'nrbTxsQuantity',
    fieldName: 'nrbTxsQuantity',
    minWidth: 100,
  },
  {
    key: 'calcResultsQuantity',
    name: 'Events Qty.',
    ariaLabel: 'calcResultsQuantity',
    fieldName: 'calcResultsQuantity',
    minWidth: 100,
  },
  {
    key: 'deltaQuantity',
    name: 'Delta Qty.',
    ariaLabel: 'deltaQuantity',
    fieldName: 'deltaQuantity',
    minWidth: 100,
  },
];

function getListItems(data) {
  const { expectationsBreakdown, stagedTransactionsBreakdown, nrbBreakdown, calcResultsBreakdown } =
    data;

  const productIds = Object.keys(expectationsBreakdown.totalQuantityByProducts);

  const listItems = productIds
    .map(productId => {
      const stagingQuantity = expectationsBreakdown.totalQuantityByProducts[productId];
      const stagedTxsQuantity = stagedTransactionsBreakdown.totalQuantityByProducts[productId] || 0;
      const nrbTxsQuantity = nrbBreakdown.totalQuantityByProducts[productId] || 0;
      const calcResultsQuantity = calcResultsBreakdown.totalQuantityByProducts[productId] || 0;
      const totalIngestedQuantity = stagedTxsQuantity + nrbTxsQuantity + calcResultsQuantity;
      const deltaQuantity = stagingQuantity - totalIngestedQuantity;

      return {
        productId,
        stagingQuantity,
        stagedTxsQuantity,
        nrbTxsQuantity,
        calcResultsQuantity,
        deltaQuantity,
      };
    })
    .filter(({ deltaQuantity }) => deltaQuantity > 0);

  return listItems;
}

export function shouldShowProductReIngestionRecommendation(data) {
  return getListItems(data).length > 0;
}

function useCreateProductTrackersMutation() {
  return useMutation('createProductTrackers', async ({ month, productGuids }) => {
    const { lastProcessedId } = await sapi.get(`/dri/last-processed-id/${month}`);

    await Promise.all(
      productGuids.map(productGuid =>
        sapi.post('/trackers/products', {
          Name: 'Store2TxByProductIngestion',
          ProductId: productGuid,
          LastProcessedIdentifier: lastProcessedId,
          StartDate: moment.utc(month).startOf('month').toDate().toISOString(),
          EndDate: moment.utc(month).endOf('month').toDate().toISOString(),
          SnoozedTillTimestamp: moment.utc().subtract(3, 'days').toDate().toISOString(),
          PartitionName: '3p-tla',
          ProcessingFlag: 'I',
          ProcessingStatus: 'InProgress',
          LastModifiedReason: `created tracker for product for missing transactions in month ${month} using DRI toolkit.`,
        }),
      ),
    );
  });
}

export default function ProductReIngestionRecommendation({ data }) {
  const { month } = data;
  const listItems = useMemo(() => getListItems(data), [data]);
  const createProductTrackersMutation = useCreateProductTrackersMutation();
  const restartIngestorMutation = useRestartIngestorMutation();

  return (
    <Stack
      style={{
        display: 'inline-flex',
        backgroundColor: 'white',
        boxShadow: Depths.depth4,
        padding: 20,
      }}
      tokens={{ childrenGap: 14 }}
    >
      <Text style={{ fontWeight: 'bold' }}>Re-Ingest Transactions for specific Products</Text>

      <Text style={{ fontWeight: 'normal' }}>
        There are serval transactions missing for this contract. Below is a list of products for
        this contract which are missing transactions. Use the button below to re-ingest these
        products for this month.
      </Text>

      <DetailsList
        styles={{
          contentWrapper: {
            maxHeight: 140,
          },
        }}
        selectionMode={SelectionMode.none}
        compact
        items={listItems}
        columns={columns}
      />

      {createProductTrackersMutation.error && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentError }}>
          {createProductTrackersMutation.error.response?.data?.message ||
            createProductTrackersMutation.error.message}
        </Text>
      )}

      {createProductTrackersMutation.isSuccess && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentTeal }}>
          <Icon iconName="StatusCircleCheckmark" />
          Successfully created product trackers. Proceed to the next step of restarting ingestor to
          kick off ingestion.
        </Text>
      )}

      {restartIngestorMutation.error && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentError }}>
          {restartIngestorMutation.error.response?.data?.message ||
            restartIngestorMutation.error.message}
        </Text>
      )}

      {restartIngestorMutation.isSuccess && (
        <Text style={{ fontWeight: 'normal', color: theme.palette.accentTeal }}>
          <Icon iconName="StatusCircleCheckmark" />
          Successfully restarted ingestor. Give it some time to send the transactions (~10 minutes),
          then re-submit the form above. If issues persist, then get help from an SME on the team.
        </Text>
      )}

      <Stack horizontal tokens={{ childrenGap: 14 }}>
        <Stepper number={1} disabled={createProductTrackersMutation.isSuccess}>
          <PrimaryButton
            disabled={
              createProductTrackersMutation.isSuccess || createProductTrackersMutation.isLoading
            }
            onClick={() =>
              createProductTrackersMutation.mutate({
                month,
                productGuids: listItems.map(item => item.productId),
              })
            }
          >
            {createProductTrackersMutation.isLoading ? (
              <Spinner size={SpinnerSize.xSmall} />
            ) : (
              'Create Product Trackers'
            )}
          </PrimaryButton>
        </Stepper>

        <Stepper number={2} disabled={!createProductTrackersMutation.isSuccess}>
          <PrimaryButton
            disabled={!createProductTrackersMutation.isSuccess || restartIngestorMutation.isLoading}
            onClick={() => restartIngestorMutation.mutate()}
          >
            {restartIngestorMutation.isLoading ? (
              <Spinner size={SpinnerSize.xSmall} />
            ) : (
              'Restart Ingestor'
            )}
          </PrimaryButton>
        </Stepper>
      </Stack>
    </Stack>
  );
}

ProductReIngestionRecommendation.propTypes = {
  data: PropTypes.shape({}).isRequired,
};
